export type ChatEntryWithReactId = {
  reactId?: unknown;
  completionRevision?: unknown;
  updatedDate?: unknown;
};

/** An acknowledgement cannot replace a saved, versioned completion. */
export function isStaleCompletionResponse(current: ChatEntryWithReactId | undefined, incoming: ChatEntryWithReactId): boolean {
  if (!current || current.reactId !== incoming.reactId) return false;
  const revision = current.completionRevision;
  if (typeof revision !== "number" || !Number.isSafeInteger(revision) || revision < 1) return false;
  const next = incoming.completionRevision;
  if (typeof next !== "number" || !Number.isSafeInteger(next) || next < revision) return true;
  return next === revision && typeof current.updatedDate === "number" && Number.isFinite(current.updatedDate)
    && (typeof incoming.updatedDate !== "number" || !Number.isFinite(incoming.updatedDate) || incoming.updatedDate <= current.updatedDate);
}

export function findChatEntryIndexByReactId(
  chatHistory: readonly ChatEntryWithReactId[],
  reactId: unknown,
): number {
  if (typeof reactId !== "string" || reactId.length === 0) return -1;
  return chatHistory.findIndex((entry) => entry?.reactId === reactId);
}

export function replaceChatEntryByReactId<T extends ChatEntryWithReactId>(
  chatHistory: readonly T[],
  reactId: unknown,
  replacement: T,
): { chatHistory: T[]; replaced: boolean } {
  const nextHistory = [...chatHistory];
  const entryIndex = findChatEntryIndexByReactId(nextHistory, reactId);
  if (entryIndex < 0) {
    return { chatHistory: nextHistory, replaced: false };
  }

  nextHistory[entryIndex] = replacement;
  return { chatHistory: nextHistory, replaced: true };
}

export function patchChatEntryByReactId<T extends ChatEntryWithReactId>(
  chatHistory: readonly T[],
  reactId: unknown,
  patch: Record<string, unknown>,
): { chatHistory: T[]; patched: boolean } {
  const entryIndex = findChatEntryIndexByReactId(chatHistory, reactId);
  if (entryIndex < 0) return { chatHistory: [...chatHistory], patched: false };

  const nextHistory = [...chatHistory];
  nextHistory[entryIndex] = { ...nextHistory[entryIndex], ...patch } as T;
  return { chatHistory: nextHistory, patched: true };
}

export function removeChatEntryByReactId<T extends ChatEntryWithReactId>(
  chatHistory: readonly T[],
  reactId: unknown,
): { chatHistory: T[]; removed: boolean } {
  const entryIndex = findChatEntryIndexByReactId(chatHistory, reactId);
  if (entryIndex < 0) return { chatHistory: [...chatHistory], removed: false };

  const nextHistory = [...chatHistory];
  nextHistory.splice(entryIndex, 1);
  return { chatHistory: nextHistory, removed: true };
}
